import { workspaceSettingsFormSchema } from '@stratumn/studio-shared';
import { useForm } from '@tanstack/react-form';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { ProcessTagBadge } from '@/components/process-tag-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import useProcess from '@/hooks/useProcess';
import useStores from '@/hooks/useStore';
import { extractErrorMessage } from '@/services/errors/errors';

const NONE_VALUE = '__none__';

type Props = {
  processId: string;
};

export const WorkspaceSettingsCard = observer(({ processId }: Props) => {
  const process = useProcess();
  const { processStore, organizationAdminStore } = useStores();

  // Read the observable list in the component body so this `observer` tracks it
  // and re-renders when fetchOrganizations() resolves. Reading it only inside
  // the <form.Field> render-prop below is NOT tracked (that prop runs in
  // TanStack Form's own non-observer component), which left the Select empty
  // after a page reload until something else forced a re-render.
  const organizations = organizationAdminStore.organizations;

  useEffect(() => {
    organizationAdminStore.fetchOrganizations().catch((error: unknown) => {
      toast.error(
        `Failed to load organizations: ${extractErrorMessage(error)}`
      );
    });
  }, [organizationAdminStore]);

  const form = useForm({
    defaultValues: {
      gitlabRepositoryId: process?.gitlabRepositoryId ?? -1,
      organizationName: process?.organizationName ?? '',
      workspaceActivated: process?.workspaceActivated ?? false
    },
    validators: {
      onSubmit: workspaceSettingsFormSchema
    },
    onSubmit: async ({ value }) => {
      // The linked organization's type is the environment tag. The frontend
      // already has it from the loaded list, so send it directly instead of
      // re-deriving it on the backend. The type is captured at selection time;
      // re-selecting the org refreshes it if it ever changes.
      const selectedOrg = organizationAdminStore.organizations.find(
        org => org.name === value.organizationName
      );
      await processStore.adminUpdateProcess(processId, {
        gitlabRepositoryId: value.gitlabRepositoryId,
        organizationName: value.organizationName,
        workspaceActivated: value.workspaceActivated,
        tag: selectedOrg?.organizationType ?? null
      });
      toast.success('Workspace settings saved');
    },
    onSubmitInvalid: ({ formApi }) => {
      const errors = formApi.state.errorMap.onSubmit;
      if (errors) {
        const errorList = Array.isArray(errors) ? errors : [errors];
        const messages = errorList
          .flatMap(e => {
            if (e && typeof e === 'object' && 'message' in e) {
              return [e.message];
            }
            if (typeof e === 'string') {
              return [e];
            }
            return [];
          })
          .filter(Boolean);
        toast.error(
          messages.length > 0 ? messages.join(', ') : 'Invalid form values'
        );
      }
    }
  });

  if (!process) {
    return null;
  }

  return (
    <div className="rounded-xl bg-card p-6 shadow-verail">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-medium text-lg">Workspace Settings</h3>
        <Badge
          variant={
            process.isWorkspaceFeatureEnabled ? 'pastelGreen' : 'pastelYellow'
          }
        >
          {process.isWorkspaceFeatureEnabled ? 'Active' : 'Inactive'}
        </Badge>
      </div>
      <form
        className="flex flex-col gap-4"
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.Field name="gitlabRepositoryId">
          {field => (
            <div className="flex flex-col gap-2">
              <Label htmlFor="ws-gitlab-repo-id">GitLab Repository ID</Label>
              <Input
                id="ws-gitlab-repo-id"
                name={field.name}
                onChange={e => {
                  const parsed = Number.parseInt(e.target.value, 10);
                  field.handleChange(Number.isNaN(parsed) ? -1 : parsed);
                }}
                placeholder="-1"
                type="number"
                value={String(field.state.value)}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="organizationName">
          {field => {
            const currentValue = field.state.value;
            // Only flag a saved org as "not listed" once the list has loaded;
            // otherwise it briefly looks orphaned during the initial fetch.
            const isOrphan =
              currentValue !== '' &&
              organizations.length > 0 &&
              !organizations.some(org => org.name === currentValue);
            return (
              <div className="flex flex-col gap-2">
                <Label htmlFor="ws-organization-name">Organization</Label>
                <Select
                  onValueChange={value =>
                    field.handleChange(value === NONE_VALUE ? '' : value)
                  }
                  value={currentValue === '' ? NONE_VALUE : currentValue}
                >
                  <SelectTrigger id="ws-organization-name">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>None</SelectItem>
                    {isOrphan && (
                      <SelectItem value={currentValue}>
                        {currentValue} (not listed)
                      </SelectItem>
                    )}
                    {organizations.map(org => (
                      <SelectItem key={org.rowId} value={org.name}>
                        <span className="flex items-center gap-2">
                          {org.name}
                          <ProcessTagBadge tag={org.organizationType} />
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  Must not be the same as the main client organization.
                </p>
              </div>
            );
          }}
        </form.Field>

        <form.Field name="workspaceActivated">
          {field => (
            <div className="flex items-center justify-between">
              <form.Subscribe
                selector={state =>
                  [state.canSubmit, state.isDirty, state.isSubmitting] as const
                }
              >
                {([canSubmit, isDirty, isSubmitting]) => (
                  <Button
                    disabled={!(canSubmit && isDirty) || isSubmitting}
                    size="sm"
                    type="submit"
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </form.Subscribe>
              <div className="flex items-center gap-2">
                <Label htmlFor="ws-activate-toggle">Activate Workspace</Label>
                <Switch
                  checked={field.state.value}
                  id="ws-activate-toggle"
                  onCheckedChange={v => field.handleChange(v)}
                />
              </div>
            </div>
          )}
        </form.Field>
      </form>
    </div>
  );
});
